import type { Industry } from '../../../types/account';
import { teamHeading } from '../../../lib/portalTeamHeading';
import { teamInitials, type PortalTeamMember } from '../../../lib/portalTeam';

// CLIENT PORTAL §4/§5 — the Kyck people on this client's account.
//
// ⚠️⚠️ A MISSING PHOTO DEGRADES TO A MONOGRAM, NEVER TO A BROKEN IMAGE AND NEVER
// TO A SUBSTITUTED FACE. The map in `portalTeam.ts` is explicit precisely so a
// name with no entry produces NOTHING to load; this is what renders in its
// place. A monogram is an honest placeholder — an inferred asset is not.
//
// ⚠️ AND A PHOTO THAT FAILS TO LOAD FALLS BACK TO THE SAME MONOGRAM. The files
// depend on a Dockerfile `COPY public ./public`, which is exactly the kind of
// thing that works in dev and 404s in production; the brand-lockup note records
// that trap. `onError` is the second line of defence behind the explicit map.

/** The circular photo, or the initials. */
function Portrait({ member, size }: { member: PortalTeamMember; size: 'lg' | 'sm' }) {
  const px = size === 'lg' ? 'h-16 w-16 text-lg' : 'h-10 w-10 text-xs';
  if (member.photoUrl) {
    return (
      <img
        src={member.photoUrl}
        // ⚠️ EMPTY ALT, and deliberately: the name is rendered as text directly
        // beside it, so alt text here would make a screen reader say it twice.
        // A decorative image is the correct classification when the information
        // it carries is already in the DOM.
        alt=""
        className={`${px} shrink-0 rounded-full object-cover`}
        onError={(e) => {
          // ⚠️ Replace the element rather than leaving a broken frame. Hiding it
          // loses the monogram too, so the parent's fallback is revealed by
          // marking the image failed rather than by unmounting it.
          e.currentTarget.dataset.failed = 'true';
          e.currentTarget.style.display = 'none';
          const sib = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (sib) sib.style.display = '';
        }}
      />
    );
  }
  return null;
}

function Avatar({ member, size }: { member: PortalTeamMember; size: 'lg' | 'sm' }) {
  const px = size === 'lg' ? 'h-16 w-16 text-lg' : 'h-10 w-10 text-xs';
  return (
    <>
      <Portrait member={member} size={size} />
      <span
        aria-hidden="true"
        // Hidden behind the photo when there is one; revealed by `onError`.
        style={member.photoUrl ? { display: 'none' } : undefined}
        className={`${px} inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground`}
      >
        {teamInitials(member.name)}
      </span>
    </>
  );
}

/**
 * The client's own account manager — the large block.
 *
 * ⚠️ A NULL PHONE RENDERS NO PHONE LINE AT ALL. Never a fallback to another
 * number, never the office line dressed as a direct one.
 */
export function AccountManagerCard({ member }: { member: PortalTeamMember }) {
  return (
    <div className="flex items-start gap-4 rounded-lg border border-border bg-card px-5 py-5">
      <Avatar member={member} size="lg" />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">
          Your account manager
        </p>
        <p className="mt-0.5 font-semibold text-foreground">{member.name}</p>
        {/* ⚠️ THE TITLE SITS UNDER THE NAME even here, where the block is already
            labelled "Your account manager" — because for a client whose own manager
            is Ethan the two are not the same statement ("NZ Account Manager"), and a
            title that appears on the list but vanishes on the main block reads as
            missing rather than redundant. */}
        <p className="text-sm text-muted-foreground">{member.title}</p>
        <p className="mt-1 text-sm">
          <a className="text-foreground underline underline-offset-2" href={`mailto:${member.email}`}>
            {member.email}
          </a>
        </p>
        {member.phone && (
          <p className="text-sm">
            <a className="text-foreground underline underline-offset-2" href={`tel:${member.phone.replace(/\s+/g, '')}`}>
              {member.phone}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Everyone else, smaller (Scott: "at a smaller size than the client's own AM").
 *
 * ⚠️ RENDERS NOTHING WHEN THE LIST IS EMPTY, rather than a heading over a blank
 * space. An empty list is the normal state for a client whose industry has one
 * account manager, not a fault to announce.
 */
export function TeamList({
  members,
  industries,
}: {
  members: readonly PortalTeamMember[];
  /**
   * The industries the LIST was derived from (§3a).
   *
   * ⚠️⚠️ THE HEADING IS A FUNCTION OF THE SAME VALUE, never a fixed string.
   * Scott asked for "Other Fitness Franchise Contacts", and hardcoding it would
   * undo round 9 §5: the team is derived from the VIEWING CLIENT'S industry so an
   * automotive portal needs no second rule, and a fitness word on an automotive
   * client's page is that guarantee failing where a client would see it.
   */
  industries: readonly Industry[];
}) {
  if (members.length === 0) return null;
  return (
    <div className="mt-6">
      <h2 className="text-xs uppercase tracking-wide text-muted-foreground">
        {teamHeading(industries)}
      </h2>
      <ul className="mt-3 grid gap-3 sm:grid-cols-2">
        {members.map((m) => (
          <li key={m.email} className="flex items-center gap-3">
            <Avatar member={m} size="sm" />
            <div className="min-w-0 text-sm">
              <p className="truncate font-medium text-foreground">{m.name}</p>
              <p className="truncate text-muted-foreground">{m.title}</p>
              <p className="truncate">
                <a className="text-muted-foreground underline underline-offset-2" href={`mailto:${m.email}`}>
                  {m.email}
                </a>
              </p>
              {m.phone && (
                <p className="truncate">
                  <a className="text-muted-foreground underline underline-offset-2" href={`tel:${m.phone.replace(/\s+/g, '')}`}>
                    {m.phone}
                  </a>
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
